import { useEffect, useState } from 'react';
import { FilterBar } from '@/components/filter-bar';
import { SkuTable } from '@/components/data-table';
import { Pagination } from '@/components/pagination';
import { SkuDetailModal } from '@/components/sku-detail-modal';
import { useFilterState } from '@/hooks/use-filter-state';
import { useMonitorList } from '@/hooks/use-monitor-list';
import { getBrands } from '@/lib/api';
import type { Monitor, BrandStat } from '@/lib/api';
import { PAGE_LIMIT } from '@/lib/constants';

export interface PriceListRouteProps {
  brands: BrandStat[];
  onBrandsLoaded: (b: BrandStat[]) => void;
}

const TABLE_PAGE_LIMIT = 50;

export default function PriceListRoute({ brands, onBrandsLoaded }: PriceListRouteProps) {
  const { filter, page, onFilterChange, onReset, onPageChange, setSort } = useFilterState();
  const { data, meta, loading, error } = useMonitorList(filter, page, TABLE_PAGE_LIMIT);
  const [detailMonitor, setDetailMonitor] = useState<Monitor | null>(null);

  useEffect(() => {
    if (brands.length > 0) return;
    let cancelled = false;
    getBrands()
      .then((b) => !cancelled && onBrandsLoaded(b))
      .catch((err) => console.error('Brands load failed:', err));
    return () => {
      cancelled = true;
    };
  }, [brands.length, onBrandsLoaded]);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Price List</h1>
          <p className="text-sm text-muted-foreground">
            Bảng giá đầy đủ — sortable, filter, sticky SKU column
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          {loading ? '...' : `${meta.total.toLocaleString('vi-VN')} SKUs`}
        </div>
      </header>

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <FilterBar state={filter} brands={brands} onChange={onFilterChange} onReset={onReset} />
      </div>

      <SkuTable
        monitors={data}
        loading={loading}
        error={error}
        sort={filter.sort}
        onSortChange={setSort}
        onSelect={setDetailMonitor}
      />

      <Pagination meta={meta} pageSize={PAGE_LIMIT < TABLE_PAGE_LIMIT ? TABLE_PAGE_LIMIT : PAGE_LIMIT} onPageChange={onPageChange} />

      <SkuDetailModal
        monitor={detailMonitor}
        open={!!detailMonitor}
        onOpenChange={(o) => !o && setDetailMonitor(null)}
      />
    </div>
  );
}
